import {useCallback, useEffect, useMemo, useState} from 'react'
import {
  useTable,
  UseSortByColumnProps,
  ColumnInstance,
  Row,
  useSortBy,
  TableInstance,
} from 'react-table'
import {KTCard, KTCardBody} from '../../../_metronic/helpers'
import {KTIcon} from '../../../_metronic/helpers'
import {Loader} from '../product-master/loader'
import {getInventoryAgeingColumns} from './columns'
import {
  InventoryAgeingRecord,
  WarehouseOption,
  ProductOption,
  getWarehouseOptions,
  getProductOptions,
  getInventoryAgeingReport,
} from './api'
import clsx from 'clsx'
import {useLayout} from '../../../_metronic/layout/core'
import {useNavigate} from 'react-router-dom'
import toast from 'react-hot-toast'
import * as XLSX from 'xlsx'
import {
  ALL_WAREHOUSES_OPTION,
  ReportFilters,
  ReportSelectOption,
} from '../inventory/components/ReportFilters'
import {ReportPagination} from '../inventory/components/ReportPagination'
import {REPORT_PAGE_SIZE} from '../inventory/components/reportTypes'
import {ReportDownloadButton, ReportRefreshButton} from '../shared/ui/ReportHeaderActions'

// ─── Types ────────────────────────────────────────────────────────────────────

type SortableColumn = ColumnInstance<InventoryAgeingRecord> &
  UseSortByColumnProps<InventoryAgeingRecord>
type TableInstanceWithHooks = TableInstance<InventoryAgeingRecord> & {
  headers: Array<
    ColumnInstance<InventoryAgeingRecord> & UseSortByColumnProps<InventoryAgeingRecord>
  >
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const InventoryAgeing = () => {
  const {company, financialYear, fyStartDate, fyEndDate} = useLayout()
  const navigate = useNavigate()

  // ─── Date range state ──────────────────────────────────────────────────────
  const [from, setFrom] = useState<string>('')
  const [to, setTo] = useState<string>('')
  const [isDateUpdate, setIsDateUpdate] = useState(false)
  const [isDateRangeRefreshButtonHovered, setIsDateRangeRefreshButtonHovered] = useState(false)

  // ─── Filter options ────────────────────────────────────────────────────────
  const [warehouseOptions, setWarehouseOptions] = useState<ReportSelectOption[]>([])
  const [productOptions, setProductOptions] = useState<ReportSelectOption[]>([])
  const [selectedWarehouse, setSelectedWarehouse] =
    useState<ReportSelectOption>(ALL_WAREHOUSES_OPTION)
  const [selectedProducts, setSelectedProducts] = useState<ReportSelectOption[]>([])

  // ─── Table data ────────────────────────────────────────────────────────────
  const [data, setData] = useState<InventoryAgeingRecord[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const columns = useMemo(() => getInventoryAgeingColumns(), [])

  const {getTableProps, getTableBodyProps, headers, rows, prepareRow} = useTable(
    {columns, data},
    useSortBy
  ) as TableInstanceWithHooks

  // ─── Guard: redirect if no company/FY ─────────────────────────────────────
  useEffect(() => {
    if (!company || !company.value || !financialYear || !financialYear.value) {
      navigate('/companies')
    }
  }, [company?.value, financialYear?.value])

  // ─── Date range initialisation ────────────────────────────────────────────
  useEffect(() => {
    if (fyStartDate && fyEndDate) {
      setFrom(fyStartDate)
      setTo(fyEndDate)
    } else {
      const storedFrom = localStorage.getItem('fromDate')
      const storedTo = localStorage.getItem('toDate')
      if (!storedFrom || !storedTo) {
        if (!financialYear?.value) return
        const [year1, year2] = financialYear.value.split('-')
        setFrom(new Date(Number(year1.trim()), 3, 2).toISOString().split('T')[0])
        setTo(new Date(Number(year2.trim()), 3, 1).toISOString().split('T')[0])
      } else {
        setFrom(storedFrom)
        setTo(storedTo)
      }
    }
    setIsDateUpdate(true)
  }, [fyStartDate, fyEndDate])

  // ─── Persist date range to localStorage ───────────────────────────────────
  useEffect(() => {
    if (isDateUpdate) {
      localStorage.setItem('fromDate', from)
      localStorage.setItem('toDate', to)
    }
  }, [isDateUpdate])

  // ─── Load filter options ───────────────────────────────────────────────────
  useEffect(() => {
    if (company?.value) {
      getWarehouseOptions(company.value)
        .then((res) => {
          if (res)
            setWarehouseOptions(
              res.map((w: WarehouseOption) => ({label: w.warehouseName, value: w.warehouseId}))
            )
        })
        .catch((err) => toast.error(err?.detail || 'Failed to load warehouses'))

      getProductOptions(company.value)
        .then((res) => {
          if (res)
            setProductOptions(
              res.map((p: ProductOption) => ({label: p.productName, value: p.productId}))
            )
        })
        .catch((err) => toast.error(err?.detail || 'Failed to load products'))
    }
  }, [company?.value])

  // ─── Fetch report data ─────────────────────────────────────────────────────
  const getData = useCallback(async () => {
    if (company?.value && financialYear?.value && from && to && isDateUpdate) {
      try {
        setIsError(false)
        setIsLoading(true)
        const res = await getInventoryAgeingReport({
          company_id: Number(company.value),
          fy_name: financialYear.value,
          from_date: from,
          to_date: to,
          warehouse_ids:
            selectedWarehouse.value > 0 ? [selectedWarehouse.value] : null,
          product_ids: selectedProducts.length > 0 ? selectedProducts.map((p) => p.value) : null,
          product_line: '',
          movement_days: 0,
          page: currentPage,
          page_size: REPORT_PAGE_SIZE,
        })
        if (res) {
          setData(res.records)
          setCurrentPage(res.page)
          setTotalPages(res.totalPages)
          setTotalRecords(res.totalRecords)
        }
      } catch (err: any) {
        toast.error(err?.detail || err?.message || 'Something went wrong')
        setData([])
        setTotalPages(1)
        setTotalRecords(0)
        setIsError(true)
      } finally {
        setIsLoading(false)
        setIsDateUpdate(false)
      }
    }
  }, [
    company?.value,
    financialYear?.value,
    from,
    to,
    isDateUpdate,
    currentPage,
    selectedWarehouse,
    selectedProducts,
  ])

  useEffect(() => {
    getData()
  }, [getData])

  // ─── Download handlers ─────────────────────────────────────────────────────
  const handleDownloadCSV = () => {
    toast.success('Download has been started successfully')
    const ws = XLSX.utils.json_to_sheet(
      data.map((r) => ({
        Product: r.productName,
        'SKU Code': r.skuCode,
        '0-30 Days': r.d0To30,
        '31-60 Days': r.d31To60,
        '61-90 Days': r.d61To90,
        '90+ Days': r.d90Plus,
        UOM: r.unit,
      }))
    )
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory Ageing')
    XLSX.writeFile(wb, `Inventory-Ageing_${company.label}_${financialYear.value}.xlsx`)
  }

  const handleDownloadPDF = () => {
    toast.success('Download has been started successfully')
    // PDF export placeholder — wire up backend PDF endpoint when available
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Card 1 — Date range + refresh + download buttons */}
      <KTCard className='mb-5'>
        <div className='card-header border-0'>
          <div className='card-title'>
            <div className='d-flex align-items-center position-relative my-1'>
              <input
                type='date'
                className='form-control form-control-solid w-250px ps-14 me-3'
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
              <input
                type='date'
                className='form-control form-control-solid w-250px ps-14 me-3'
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
              <ReportRefreshButton
                hovered={isDateRangeRefreshButtonHovered}
                onMouseEnter={() => setIsDateRangeRefreshButtonHovered(true)}
                onMouseLeave={() => setIsDateRangeRefreshButtonHovered(false)}
                onClick={() => {
                  if (!from || !to)
                    toast.error('Please make sure both the From and To dates are selected')
                  else setIsDateUpdate(true)
                }}
              />
            </div>
          </div>

          <div className='card-toolbar'>
            <div className='d-flex justify-content-end gap-3' data-kt-user-table-toolbar='base'>
              <ReportDownloadButton
                disabled={!data.length}
                onClick={handleDownloadCSV}
                label='Download CSV'
              />
              <ReportDownloadButton
                disabled={!data.length}
                onClick={handleDownloadPDF}
                label='Download PDF'
              />
            </div>
          </div>
        </div>
      </KTCard>

      {/* Card 2 — Filters row + table */}
      <KTCard>
        <ReportFilters
          warehouseOptions={warehouseOptions}
          selectedWarehouse={selectedWarehouse}
          onWarehouseChange={setSelectedWarehouse}
          productOptions={productOptions}
          selectedProducts={selectedProducts}
          onProductsChange={setSelectedProducts}
          onApply={() => {
            if (!from || !to) {
              toast.error('Please make sure both From and To dates are selected')
              return
            }

            setCurrentPage(1)
            setIsDateUpdate(true)
          }}
        />

        <KTCardBody className='py-4'>
          {!isLoading && (
            <div className='table-responsive'>
              <table
                className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
                {...getTableProps()}
              >
                <thead>
                  <tr className='text-start text-muted fw-bolder bg-light ps-4 fs-7 text-uppercase gs-0'>
                    {headers.map((column: SortableColumn) => (
                      <CustomHeaderColumn key={column.id} column={column} />
                    ))}
                  </tr>
                </thead>
                <tbody className='text-gray-600 text-center fw-bold' {...getTableBodyProps()}>
                  {rows.length > 0 ? (
                    rows.map((row: Row<InventoryAgeingRecord>, i) => {
                      prepareRow(row)
                      return <CustomRow row={row} key={`row-${i}-${row.id}`} />
                    })
                  ) : (
                    <tr>
                      <td colSpan={7}>
                        <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                          No matching records found
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {isLoading && <Loader isError={isError} />}
          {!isLoading && (
            <ReportPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              pageSize={REPORT_PAGE_SIZE}
              onPageChange={(page) => {
                setCurrentPage(page)
                setIsDateUpdate(true)
              }}
            />
          )}
        </KTCardBody>
      </KTCard>
    </>
  )
}

// ─── Table Sub-components ─────────────────────────────────────────────────────

type ColProps = {
  column: ColumnInstance<InventoryAgeingRecord> & UseSortByColumnProps<InventoryAgeingRecord>
}

export const CustomHeaderColumn: React.FC<ColProps> = ({column}) => (
  <>
    {column.Header && typeof column.Header === 'string' ? (
      <th {...column.getHeaderProps(column.getSortByToggleProps())} className='cursor-pointer'>
        {column.render('Header')}
        <span className='ms-1'>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
      </th>
    ) : (
      column.render('Header')
    )}
  </>
)

type RowProps = {
  row: Row<InventoryAgeingRecord>
}

const CustomRow: React.FC<RowProps> = ({row}) => (
  <tr {...row.getRowProps()} className='bg-hover-light'>
    {row.cells.map((cell) => (
      <td
        {...cell.getCellProps()}
        className={clsx({'text-end min-w-100px': cell.column.id === 'actions'})}
      >
        {cell.render('Cell')}
      </td>
    ))}
  </tr>
)
